import axios from 'axios'
import router from '@/router'
import { sendOtp, verifyOtp } from '@/store/api'
import * as Sentry from '@sentry/browser'
import { identify as identifyLaunchDarkly } from '@/lib/launchDarkly'
import { te } from '@/locales'

let requestErrorTimeout = null

const defaultPasswordStatus = {
  required: false,
  title: null,
  incorrect: false,
  password: sessionStorage.getItem('password'),
  errorMessage: null,
  errorCode: 0
}

if (defaultPasswordStatus.password) {
  axios.defaults.headers.common['X-Password'] = defaultPasswordStatus.password
}

const defaultOtp = {
  required: false,
  verified: false,
  sessionUid: sessionStorage.getItem('session_uid'),
  mobileLastFour: sessionStorage.getItem('session_last4'),
  codeExpires: null,
  requestError: null,
  verifyError: null
}

if (defaultOtp.sessionUid) {
  axios.defaults.headers.common['X-Session-Uid'] = defaultOtp.sessionUid
}

const authModule = {
  namespaced: true,
  state: {
    isAuthenticated: false,
    authorising: false,
    user: {},
    passwordStatus: { ...defaultPasswordStatus },
    otp: {
      ...defaultOtp,
      codesSent: 0
    }
  },
  mutations: {
    SET_AUTHENTICATED (state) {
      state.isAuthenticated = true
    },
    SET_AUTHORISING (state, authorising) {
      state.authorising = authorising
    },
    SET_USER (state, user) {
      state.user = user
    },
    SET_PASSWORD_STATUS (state, passwordStatus) {
      state.passwordStatus = {
        ...state.passwordStatus,
        ...passwordStatus
      }
    },
    RESET_PASSWORD_STATUS (state) {
      state.passwordStatus = { ...defaultPasswordStatus }
    },
    SET_OTP_REQUIRED (state) {
      state.otp = {
        ...state.otp,
        required: true,
        verified: false
      }
    },
    SET_OTP_SESSION (state, { sessionUid, mobileLastFour }) {
      state.otp = {
        ...state.otp,
        sessionUid,
        mobileLastFour
      }
    },
    SET_CODE_SENT (state, expires) {
      state.otp = {
        ...state.otp,
        codeExpires: expires,
        codesSent: state.otp.codesSent + 1
      }
    },
    CLEAR_OTP_ERRORS (state) {
      state.otp = {
        ...state.otp,
        requestError: null,
        verifyError: null
      }
    },
    SET_OTP_REQUEST_ERROR (state, error) {
      state.otp = {
        ...state.otp,
        requestError: error
      }
    },
    SET_OTP_VERIFY_ERROR (state, error) {
      state.otp = {
        ...state.otp,
        verifyError: error
      }
    },
    SET_OTP_VERIFIED (state) {
      state.otp = {
        ...state.otp,
        verified: true,
        requestError: null,
        verifyError: null
      }
    },
    RESET_OTP (state) {
      state.otp = {
        ...defaultOtp
      }
    }
  },
  actions: {
    setAccessTokenExpiry ({ commit }, value) {
      const expiry = new Date()
      expiry.setSeconds(expiry.getSeconds() + value.expires_in)
      sessionStorage.setItem('access_token_expiry', expiry.getTime())
    },
    setAccessToken ({ commit }, value) {
      sessionStorage.setItem('access_token', JSON.stringify(value))
    },
    removeAccessToken () {
      sessionStorage.removeItem('access_token')
      sessionStorage.removeItem('access_token_expiry')
    },
    setAuthenticationValues ({ commit, state }, accessToken) {
      commit('SET_AUTHENTICATED')
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
      if (state.otp.sessionUid) {
        axios.defaults.headers.common['X-Session-Uid'] = state.otp.sessionUid
      }
    },
    setPasswordRequired ({ commit }, { errorMessage, errorCode, incorrect = false }) {
      commit('SET_PASSWORD_STATUS', {
        required: true,
        errorMessage,
        errorCode,
        incorrect
      })
    },
    storePassword ({ commit }, password) {
      axios.defaults.headers.common['X-Password'] = password
      sessionStorage.setItem('password', password)

      commit('SET_PASSWORD_STATUS', { password, incorrect: false })
    },
    setOtpSession ({ commit }, { sessionUid, mobileLastFour }) {
      commit('SET_OTP_SESSION', {
        sessionUid,
        mobileLastFour
      })

      sessionStorage.setItem('session_uid', sessionUid)
      sessionStorage.setItem('session_last4', mobileLastFour)
      axios.defaults.headers.common['X-Session-Uid'] = sessionUid
    },
    getUser ({ commit, dispatch, state, rootGetters }, password) {
      const _password = password || state.passwordStatus.password
      const options = {}
      if (_password) {
        options.headers = { 'X-Password': _password }
        options.params = { 'passphrase': _password }
      }

      return axios({
        method: 'get',
        baseURL: process.env.VUE_APP_API_URL,
        url: 'user',
        ...options
      }).then((response) => {
        identifyLaunchDarkly({
          company_id: response.data.data ? response.data.data.company_id : response.data.company_id,
          parent_id: response.data.data ? response.data.data.parent_id : response.data.parent_id
        })

        if (response.data.unsignable) {
          if (response.data.envelope_redirect_url) {
            window.location = response.data.envelope_redirect_url
          } else {
            dispatch('setBranding', response.data, { root: true })

            commit('SET_ENVELOPE_UNSIGNABLE', {
              status: response.data.status,
              company_name: response.data.company_name
            }, { root: true })

            router.push({
              name: 'EnvelopeSigned'
            }).catch(error => {
              if (error.name !== 'NavigationDuplicated') {
                throw error
              }
            })
          }

          return
        }

        commit('SET_USER', response.data.data)
        dispatch('setBranding', response.data.data, { root: true })

        if (_password && rootGetters['featureFlags/featureEnabled']('password-protection-phase2-sp')) {
          dispatch('storePassword', _password)
        }

        Sentry.setUser({
          id: response.data.data.party_id,
          email: response.data.data.party_email,
          company_id: response.data.data.company_id
        })
      }, (error) => {
        if (_password && error.response.status === 401 && error.response.data.type !== 'password') {
          dispatch('storePassword', _password)
        }

        dispatch('requestError', error)
      })
    },
    authenticate ({ commit, dispatch }, key) {
      commit('SET_AUTHORISING', true)

      return axios({
        method: 'post',
        baseURL: process.env.VUE_APP_API_URL,
        url: '/auth',
        data: {
          key
        }
      }).then((response) => {
        dispatch('setAuthenticationValues', response.data.data.access_token)
        dispatch('setAccessToken', response.data.data)
        dispatch('setAccessTokenExpiry', response.data.data)
      }).catch((error) => {
        sessionStorage.removeItem('access_token')
        const errorResponse = {
          errorMessage: (error.response && error.response.data.message) || error.message,
          errorCode: (error.response && error.response.status) || 0
        }

        router.push({ name: 'PageError', params: errorResponse })
      }).finally(() => {
        commit('SET_AUTHORISING', false)
      })
    },
    authenticatePreview ({ commit }, key) {
      commit('SET_AUTHORISING', true)

      return axios({
        method: 'post',
        baseURL: process.env.VUE_APP_API_URL,
        url: '/auth/preview',
        data: {
          key
        }
      }).then((response) => {
        const { data: { data: { access_token: token } } } = response
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        commit('SET_AUTHENTICATED')
      }).catch((error) => {
        const errorResponse = {
          errorMessage: (error.response && error.response.data.message) || error.message,
          errorCode: (error.response && error.response.status) || 0
        }

        router.push({ name: 'PageError', params: errorResponse })
      }).finally(() => {
        commit('SET_AUTHORISING', false)
      })
    },
    resetPasswordAndOtp ({ commit }) {
      commit('RESET_PASSWORD_STATUS')
      commit('RESET_OTP')
    },
    async sendOtp ({ commit, dispatch }) {
      commit('CLEAR_OTP_ERRORS')

      try {
        const response = await sendOtp()
        commit(
          'SET_CODE_SENT',
          response && response.data && response.data.expires ? new Date(response.data.expires) : null
        )
        return true
      } catch (error) {
        dispatch('requestError', error)

        clearTimeout(requestErrorTimeout)
        // Clear error after 20 seconds
        requestErrorTimeout = setTimeout(() => {
          commit('SET_OTP_REQUEST_ERROR', null)
        }, 20000)

        return false
      }
    },
    async verifyOtp ({ commit, dispatch }, otpCode) {
      commit('CLEAR_OTP_ERRORS')

      try {
        const response = await verifyOtp(otpCode)
        commit('SET_OTP_VERIFIED')
        return response
      } catch (error) {
        if (error.response.status === 400 && error.response.data.message) {
          commit('SET_OTP_VERIFY_ERROR', error.response.data.message)
        } else if (error.response.data.message && te(error.response.data.message)) {
          commit('SET_OTP_VERIFY_ERROR', error.response.data.message)
        } else {
          dispatch('requestError', error)
        }

        clearTimeout(requestErrorTimeout)
        // Clear error after 20 seconds
        requestErrorTimeout = setTimeout(() => {
          commit('SET_OTP_REQUEST_ERROR', null)
        }, 20000)

        return false
      }
    },
    requestError: ({ commit, dispatch }, error) => {
      if (error.response.data) {
        dispatch('setBranding', error.response.data, { root: true })
      }

      if (error.response.data && error.response.data.type === 'password') {
        dispatch('setPasswordRequired', {
          errorMessage: error.response.data.message,
          errorCode: error.response.status,
          incorrect: error.response.data.status === 'incorrect'
        })
      } else if (error.response.data && error.response.data.type === 'otp') {
        if (error.response.status === 401) {
          commit('SET_OTP_REQUIRED')

          if (error.response.data.session_uid && error.response.data.party_mobile_last4) {
            dispatch('setOtpSession', {
              sessionUid: error.response.data.session_uid,
              mobileLastFour: error.response.data.party_mobile_last4
            })
          }
        } else if (error.response.status === 429) {
          commit('SET_OTP_REQUEST_ERROR', 'otp.rateLimitExceeded')
        } else if (error.response.data.message && te(error.response.data.message)) {
          commit('SET_OTP_REQUEST_ERROR', error.response.data.message)
        } else {
          commit('SET_OTP_REQUEST_ERROR', 'otp.unknownError')
        }
      } else {
        Sentry.captureException(error)

        dispatch('resetPasswordAndOtp')

        router.replace({
          name: 'PageError',
          params: {
            errorMessage: error.message || error
          }
        })
      }
    }
  },
  getters: {
    isAuthenticated: state => state.isAuthenticated,
    authorising: state => state.authorising,
    user: state => state.user,
    hasUser: state => !!state.user.party_id,
    passwordStatus: state => state.passwordStatus,
    passwordNeeded: state => state.passwordStatus.required && (!state.passwordStatus.password || state.passwordStatus.incorrect),
    otpStatus: state => state.otp,
    otpNeeded: state => state.otp.required && !state.otp.verified
  }
}

export default authModule
